import { useState, useEffect, useRef, useMemo } from "react";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { fetchSheetPatients } from "../services/sheetPatientsService";
import { findPotentialDuplicates, sameMrnNumber } from "../services/patientDuplicateMatch";
import PotentialDuplicateDialog from "./PotentialDuplicateDialog";
import { DIAGNOSIS_OPTIONS, diagnosisLabel } from "../constants/diseaseOptions";
import { authedFetch } from "../services/authedFetch";
import { PUBLIC_BASE_URL } from "../config/publicBaseUrl";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { getStagedPatientsPath, getConsentsPath } from "../services/firestorePaths";
import {
  activeSiteUsesLegacySheet,
  getActiveSiteId,
  getActiveTenantId,
} from "../services/activeTenant";
import { logoutAndClearSite } from "../services/logout";
import { useNavigate } from "react-router-dom";
import { useTenant } from "../context/TenantContext";
import { UI_REVAMP_ENABLED } from "../config/featureFlags";
import SidebarThemeToggle from "./SidebarThemeToggle";
import { Home as HomeNavIcon } from "lucide-react";
import { jsPDF } from "jspdf";
import {
  Plus,
  LogOut,
  UserCog,
  Search,
  X,
  Menu,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  UserPlus,
  Users,
  Layers,
  Workflow,
  HeartPulse,
  Phone,
  Mail,
  Calendar,
  IdCard,
  Upload,
  PenLine,
  MessageSquare,
  Send,
  CheckCircle2,
  Sparkles,
  Activity,
  Loader2,
  Eraser,
  Save,
  FileSignature,
  Clock,
  ExternalLink,
  FileText,
  ShieldCheck,
} from "lucide-react";

// Stored values; "IPF" is shown as "ILD" via diagnosisLabel (see diseaseOptions.js).
const DIAGNOSES = DIAGNOSIS_OPTIONS;

// Display only — labels, never written back.
const formatDiagnosis = (dx) =>
  Array.isArray(dx) ? dx.map(diagnosisLabel).join(", ") : diagnosisLabel(dx) || "-";

/* -------------------- Shared visuals -------------------- */
const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
  </div>
);

/* -------------------- eSignature Modal -------------------- */
function ESignatureModal({ open, onClose, patient, onSaved }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [saving, setSaving] = useState(false);

  const consentText = useMemo(
    () => `Principal Investigator: Sandeep Bansal, MD
Research Site:\n\nClinical Research Associates of Central PA
190 West Park Avenue, Suite 8
Dubois, PA 15801

You have been referred by your provider to clinical research. If you sign this authorization form, you agree to the use and disclosure (release) of your health information to the research team, as described in this Authorization Form. The following health information about you may be collected such as: Past medical history, medication history/lists, diagnoses, laboratory results including pathology, imaging results, cardiopulmonary results, operative reports, consultative reports, physician progress notes, and hospitalization reports.

Your health information may be used for screening purposes by the research site, the principal investigator, and their representatives. If you qualify for a research study this authorization gives Clinical Research Associates of Central PA permission to contact you regarding research studies available at our site.

You are required to sign this form to be prescreened for a research study. If you do not sign this form, it will not affect your treatment, or enrollment or eligibility for benefits. You may change your mind and revoke (take back) this authorization form at any time unless your health information is already disclosed based on this authorization form.

To revoke this Authorization, you must write to: \n\nLung Innovations Network, P.C.
190 West Park Avenue, Suite 8
Dubois, PA 15801
\nThis Authorization will not expire unless you revoke it.

* If this is signed by an authorized personal representative of the participant, indicate the representative’s relationship to the participant or authority to act on behalf of the participant.`,
    []
  );

  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = containerRef.current.clientWidth;
    const height = 200;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(ratio, ratio);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = "#aaa";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, height - 30);
    ctx.lineTo(width, height - 30);
    ctx.stroke();
    setHasInk(false);
  }, [open]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : null;
    return {
      x: (touch ? touch.clientX : e.clientX) - rect.left,
      y: (touch ? touch.clientY : e.clientY) - rect.top,
    };
  };

  const handleStart = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const handleMove = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };

  const handleEnd = () => setIsDrawing(false);

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const width = containerRef.current.clientWidth;
    const height = 200;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = "#aaa";
    ctx.beginPath();
    ctx.moveTo(0, height - 30);
    ctx.lineTo(width, height - 30);
    ctx.stroke();
    setHasInk(false);
  };

  const savePdfToStorage = async () => {
    if (!patient || !hasInk) {
      alert("Please sign before saving.");
      return;
    }
    setSaving(true);
    try {
      const docPdf = new jsPDF({ unit: "pt", format: "letter" });
      const margin = 48;
      const width = docPdf.internal.pageSize.getWidth();
      let y = margin;

      docPdf.setFont("helvetica", "bold").setFontSize(16);
      docPdf.text("Authorization to Share Information with Researcher", margin, y);
      y += 20;

      docPdf.setFont("helvetica", "normal").setFontSize(11);
      const ts = new Date();
      [
        ["Name", patient.name],
        ["DOB", patient.dob],
        ["MRN", patient.mrn],
        ["Diagnosis", formatDiagnosis(patient.diagnosis)],
        ["Phone", patient.phone],
        ["Email", patient.email],
        ["Signed At", ts.toLocaleString()],
      ].forEach(([label, value]) => {
        docPdf.text(`${label}: ${value || "-"}`, margin, y);
        y += 16;
      });

      y += 8;
      const wrapped = docPdf.splitTextToSize(consentText, width - margin * 2);
      wrapped.forEach((line) => {
        if (y > 700) {
          docPdf.addPage();
          y = margin;
        }
        docPdf.text(line, margin, y);
        y += 14;
      });

      y += 20;
      docPdf.setFont("helvetica", "bold").text("Signature:", margin, y);
      y += 10;
      docPdf.addImage(canvasRef.current.toDataURL("image/png"), "PNG", margin, y, 300, 80);

      const blob = docPdf.output("blob");
      const fileName = `consent_${patient.mrn || "noMRN"}_${Date.now()}.pdf`;
      const storagePath = `consents/${fileName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, blob, { contentType: "application/pdf" });
      const url = await getDownloadURL(storageRef);

      await updateDoc(doc(db, getStagedPatientsPath(),patient.id), {
        esignRequested: true,
        esignedUrl: url,
        esignedStoragePath: storagePath,
        consentFileName: fileName || "eSigned_Consent.pdf",
      });

      onSaved({ url, storagePath, fileName });
      onClose();
    } catch (err) {
      console.error("❌ Failed to save eSign consent:", err);
      alert("Could not save signed document. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 font-[Manrope]">
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(124,58,237,0.6)] text-slate-100 flex flex-col">
        <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-80 pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-purple-600/20 blur-[120px]" />
          <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-blue-600/20 blur-[120px]" />
        </div>

        {/* Header */}
        <div className="relative px-5 py-3.5 border-b border-white/10 flex items-center gap-3 shrink-0">
          <div className="relative shrink-0">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
            <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
              <FileSignature className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">
              Consent
            </div>
            <h3 className="text-base font-semibold tracking-tight text-white leading-tight truncate">
              Authorization to Share Information
            </h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/10 hover:border-white/20 flex items-center justify-center text-slate-300 hover:text-white transition shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="relative p-5 space-y-3 overflow-y-auto flex-1">
          {/* Patient meta */}
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/10">
              <Users className="w-3 h-3 text-purple-300" />
              <span className="text-slate-400">Patient</span>
              <span className="font-medium text-white">{patient?.name || "—"}</span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/10">
              <IdCard className="w-3 h-3 text-purple-300" />
              <span className="text-slate-400">MRN</span>
              <span className="font-mono text-white">{patient?.mrn || "—"}</span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/10">
              <HeartPulse className="w-3 h-3 text-purple-300" />
              <span className="text-slate-400">Dx</span>
              <span className="text-white">{formatDiagnosis(patient?.diagnosis)}</span>
            </span>
          </div>

          {/* Consent text */}
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-xs text-slate-300 whitespace-pre-line leading-6 max-h-[40vh] overflow-y-auto">
            {consentText}
          </div>

          {/* Signature pad (kept white for signature visibility) */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5 flex items-center gap-1.5">
              <PenLine className="w-3 h-3 text-purple-300" />
              Signature
            </div>
            <div
              ref={containerRef}
              className="rounded-xl border border-white/10 bg-white p-2 shadow-[0_8px_24px_-8px_rgba(124,58,237,0.5)]"
            >
              <canvas
                ref={canvasRef}
                className="w-full h-[200px] touch-none rounded-lg"
                onMouseDown={handleStart}
                onMouseMove={handleMove}
                onMouseUp={handleEnd}
                onMouseLeave={handleEnd}
                onTouchStart={handleStart}
                onTouchMove={handleMove}
                onTouchEnd={handleEnd}
              />
            </div>
            {!hasInk && (
              <div className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-purple-300/70" />
                Sign inside the box using a finger or stylus (tablet-friendly).
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="relative px-5 py-3 border-t border-white/10 bg-slate-950/50 backdrop-blur-xl flex items-center justify-end gap-2 shrink-0">
          <div className="hidden sm:flex items-center gap-1.5 mr-auto text-[10px] uppercase tracking-wider text-slate-400">
            <FileSignature className="w-3 h-3 text-purple-300" />
            eSignature PDF
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs font-medium text-slate-200 hover:text-white transition"
          >
            <Eraser className="w-3.5 h-3.5" />
            Clear
          </button>
          <button
            type="button"
            onClick={savePdfToStorage}
            disabled={saving}
            className="group relative inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 relative animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5 relative" />
            )}
            <span className="relative">
              {saving ? "Saving…" : "Save Signed PDF"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- Staged Patient Card -------------------- */
function StagedCard({ row, onChooseFile, onRequestEsign, onSend, onSendText, loadingSendId, loadingTextId }) {
  const inputRef = useRef(null);
  const diagnoses = Array.isArray(row.diagnosis)
    ? row.diagnosis
    : row.diagnosis
    ? [row.diagnosis]
    : [];

  return (
    <div className="relative group rounded-2xl border border-white/10 bg-white/[0.035] backdrop-blur-xl overflow-hidden hover:border-purple-300/30 hover:shadow-[0_10px_40px_-10px_rgba(139,92,246,0.45)] transition-all">
      <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-60 group-hover:opacity-100 transition" />
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.05] via-transparent to-blue-500/[0.05] pointer-events-none" />

      <div className="relative p-3.5 flex flex-col gap-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-sm font-bold text-white ring-1 ring-white/20 shrink-0">
              {(row.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold leading-none">
                Patient
              </div>
              <h3 className="text-sm font-semibold text-white truncate leading-tight">
                {row.name}
              </h3>
              <div className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                <Calendar className="w-3 h-3" />
                DOB {row.dob}
              </div>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/10 text-[10px] font-mono text-purple-200">
            <IdCard className="w-3 h-3 text-purple-300" />
            {row.mrn}
          </span>
        </div>

        {/* Diagnoses chips */}
        <div className="flex flex-wrap gap-1">
          {diagnoses.length === 0 ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-slate-400/10 text-slate-400 border border-slate-400/20">
              No diagnosis
            </span>
          ) : (
            diagnoses.map((d) => (
              <span
                key={d}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-500/15 border border-purple-300/30 text-purple-200"
              >
                <HeartPulse className="w-2.5 h-2.5" />
                {diagnosisLabel(d)}
              </span>
            ))
          )}
        </div>

        {/* Contact info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[11px]">
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.03] border border-white/5 text-slate-300 truncate">
            <Phone className="w-3 h-3 text-purple-300 shrink-0" />
            <span className="truncate">{row.phone || "—"}</span>
          </span>
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.03] border border-white/5 text-slate-300 truncate">
            <Mail className="w-3 h-3 text-purple-300 shrink-0" />
            <span className="truncate">{row.email || "—"}</span>
          </span>
        </div>

        {/* Status line */}
        {(row.smsLinkSent || row.consentFileName || row.esignRequested) && (
          <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
            {row.smsLinkSent && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-300/30 text-purple-200 font-semibold">
                <MessageSquare className="w-3 h-3" />
                SMS Sent
                {row.esignUrl && (
                  <a
                    href={row.esignUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-1 underline inline-flex items-center gap-0.5 hover:text-white"
                  >
                    View <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                )}
              </span>
            )}
            {!row.smsLinkSent && row.esignRequested && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/15 border border-blue-300/30 text-blue-200 font-semibold">
                <FileSignature className="w-3 h-3" />
                eSigned: {row.esignedUrl ? "Saved" : "Requested"}
              </span>
            )}
            {!row.smsLinkSent && !row.esignRequested && row.consentFileName && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-400/10 border border-white/10 text-slate-300 font-medium">
                <Upload className="w-3 h-3" />
                Selected: {row.consentFileName}
              </span>
            )}
          </div>
        )}

        {/* Action row */}
        <div className="pt-2 border-t border-white/5 flex flex-wrap items-center gap-1.5">
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => onChooseFile(row.id, e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-[11px] font-medium text-slate-200 hover:text-white transition"
          >
            <Upload className="w-3 h-3" />
            File
          </button>
          <button
            type="button"
            onClick={() => onRequestEsign(row)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-500/15 border border-blue-300/30 hover:bg-blue-500/25 text-[11px] font-medium text-blue-200 hover:text-white transition"
          >
            <PenLine className="w-3 h-3" />
            Request eSign
          </button>
          <button
            type="button"
            disabled={loadingTextId === row.id}
            onClick={() => onSendText(row)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-purple-500/15 border border-purple-300/30 hover:bg-purple-500/25 text-[11px] font-medium text-purple-200 hover:text-white transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loadingTextId === row.id ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <MessageSquare className="w-3 h-3" />
            )}
            {loadingTextId === row.id ? "Sending…" : "Send Text"}
          </button>
          <button
            type="button"
            disabled={loadingSendId === row.id}
            onClick={() => onSend(row)}
            className="group/send relative ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-[11px] font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(16,185,129,0.6)] active:scale-95 transition-all overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover/send:translate-x-full transition-transform duration-700" />
            {loadingSendId === row.id ? (
              <Loader2 className="w-3 h-3 relative animate-spin" />
            ) : (
              <Send className="w-3 h-3 relative" />
            )}
            <span className="relative">
              {loadingSendId === row.id ? "Sending…" : "Send"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- Main ReceptionPage -------------------- */
export default function ReceptionPage() {
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [mrn, setMrn] = useState("");
  const [diagnosis, setDiagnosis] = useState([]);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [staged, setStaged] = useState([]);
  const [search, setSearch] = useState("");
  const [patients, setPatients] = useState([]);
  const [loadingSendId, setLoadingSendId] = useState(null);
  const [loadingTextId, setLoadingTextId] = useState(null);
  const [esignOpen, setEsignOpen] = useState(false);
  const [esignPatient, setEsignPatient] = useState(null);
  const [isCommandDockOpen, setIsCommandDockOpen] = useState(true);
  const [mobileDockOpen, setMobileDockOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [mrnError, setMrnError] = useState("");
  const [mrnChecking, setMrnChecking] = useState(false);
  // Name + DOB duplicate warning. Non-empty means the confirmation dialog is up.
  // Advisory only. The exact-MRN check above it still blocks outright.
  const [dupMatches, setDupMatches] = useState([]);
  const formRef = useRef(null);
  const navigate = useNavigate();

  // "Back to Admin Dashboard" is admin-only, gated on the user's role FOR THE
  // ACTIVE SITE (not the legacy flat role). Owners and tenant admins qualify.
  const { isOwner, isTenantAdmin, roleForActiveSite, tenantId: activeTenantId } =
    useTenant();
  const isAdmin =
    isOwner || isTenantAdmin(activeTenantId) || roleForActiveSite === "admin";

  const stagedRef = collection(db, getStagedPatientsPath());
  const consentsRef = collection(db, getConsentsPath());

  const fetchStagedPatients = async () => {
  const snap = await getDocs(stagedRef);
  const active = snap.docs
    .map((doc) => ({ id: doc.id, ...doc.data() }))
    .filter((p) => !p.expired); // 👈 exclude expired ones
  setStaged(active);
};


  const fetchSentPatients = async () => {
    const snap = await getDocs(consentsRef);
    const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    data.sort((a, b) => (b?.signedAt?.seconds || 0) - (a?.signedAt?.seconds || 0));
    setPatients(data);
  };

  useEffect(() => {
    fetchStagedPatients();
    fetchSentPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleAddToStage = async (e) => {
    e.preventDefault();
    if (!name.trim() || !dob || !mrn.trim() || diagnosis.length === 0 || !phone.trim()) return;
    if (!/^\d{9}$/.test(mrn.trim())) {
      setMrnError("MRN must be exactly 9 digits.");
      return;
    }

    // Duplicate check across Firestore (staged + consents) and Sheets (legacy site only).
    setMrnChecking(true);
    // Same MRN by the portal's one rule (leading zeros ignored).
    const stagedDup = staged.some((p) => sameMrnNumber(p.mrn, mrn));
    const consentDup = patients.some((p) => sameMrnNumber(p.mrn, mrn));

    if (stagedDup || consentDup) {
      setMrnError("A patient with this MRN already exists.");
      setMrnChecking(false);
      return;
    }

    // Fetched once and reused by both the exact-MRN check and the name+DOB pass
    // below, so a slow sheet is hit at most once per add.
    let sheetPatients = [];
    if (activeSiteUsesLegacySheet()) {
      try {
        sheetPatients = await fetchSheetPatients();
        const sheetDup = sheetPatients.some((p) => sameMrnNumber(p.mrn, mrn));
        if (sheetDup) {
          setMrnError("A patient with this MRN already exists.");
          setMrnChecking(false);
          return;
        }
      } catch (err) {
        console.warn("Could not check sheet for duplicate MRN:", err);
        // Fail-open: allow add if the sheet check fails
        sheetPatients = [];
      }
    }

    // Second pass: the MRN is new, but is this the same PERSON already in the
    // portal under a mistyped MRN? Matches on an exact DOB plus a fuzzy name
    // (nickname, missing middle name/initial, typo). Advisory: it opens a
    // dialog asking the user to recheck the MRN rather than refusing the add.
    const matches = findPotentialDuplicates(
      { name, dob, mrn: mrn.trim() },
      [
        ...staged.map((p) => ({ ...p, sourceLabel: "Pre-Screened" })),
        ...patients.map((p) => ({ ...p, sourceLabel: "Referrals" })),
        ...sheetPatients.map((p) => ({ ...p, sourceLabel: "Legacy Sheet" })),
      ]
    );
    if (matches.length) {
      setDupMatches(matches);
      setMrnError("");
      setMrnChecking(false);
      return;
    }

    setMrnError("");
    setMrnChecking(false);
    await performAddToStage();
  };

  // The actual write, split out of handleAddToStage so "Add Anyway" on the
  // duplicate warning can reuse it without re-running the checks.
  const performAddToStage = async () => {
    setDupMatches([]);
    await addDoc(stagedRef, {
      name: name.trim(),
      dob,
      mrn: mrn.trim(),
      diagnosis,
      phone: phone.trim().startsWith("+") ? phone.trim() : `+1${phone.trim()}`,
      email: email.trim(),
      consentFileName: "",
      esignRequested: false,
      esignedUrl: "",
      esignedStoragePath: "",
      smsLinkSent: false,
      esignToken: "",
      esignUrl: "",
      createdAt: serverTimestamp(),
    });
    setName("");
    setDob("");
    setMrn("");
    setDiagnosis([]);
    setPhone("");
    setEmail("");
    fetchStagedPatients();
  };

  const handleLogout = async () => {
    await logoutAndClearSite();
    navigate("/login");
  };

  const handleChooseFile = async (id, file) => {
    if (!file) return;
    setStaged((prev) =>
      prev.map((p) => (p.id === id ? { ...p, consentFile: file, consentFileName: file.name } : p))
    );
  };

  const handleRequestEsign = (row) => {
    setEsignPatient(row);
    setEsignOpen(true);
  };

  const handleEsignSaved = ({ url, storagePath, fileName }) => {
    setStaged((prev) =>
      prev.map((p) =>
        p.id === esignPatient.id
          ? {
              ...p,
              esignRequested: true,
              esignedUrl: url,
              esignedStoragePath: storagePath,
              consentFileName: fileName || "eSigned_Consent.pdf",
            }
          : p
      )
    );
    fetchStagedPatients();
  };

  const handleSendRow = async (row) => {

  const rowDiagnosis = Array.isArray(row.diagnosis)
    ? row.diagnosis
    : row.diagnosis
    ? [row.diagnosis]
    : [];

  if (!row?.name || !row?.dob || !row?.mrn || rowDiagnosis.length === 0) return;

  try {
      setLoadingSendId(row.id);
      let finalFileUrl = "";
      if (row.esignedUrl) {
        finalFileUrl = row.esignedUrl;
      } else if (row.consentFile) {
        const storagePath = `manual_uploads/${row.consentFile.name}_${Date.now()}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, row.consentFile);
        finalFileUrl = await getDownloadURL(storageRef);
      }

      await addDoc(consentsRef, {
        name: row.name,
        dob: row.dob,
        mrn: row.mrn,
        diagnosis: rowDiagnosis,
        phone: row.phone,
        email: row.email,
        signedAt: serverTimestamp(),
        consentUrl: finalFileUrl || "",
        source: "ReceptionPage",
      });

      await deleteDoc(doc(db, getStagedPatientsPath(),row.id));
      fetchStagedPatients();
      fetchSentPatients();
    } catch (err) {
      console.error("❌ Send failed:", err);
      alert("Failed to send this patient. Please try again.");
    } finally {
      setLoadingSendId(null);
    }
  };

  const handleSendText = async (row) => {
  if (!row?.phone) {
    alert("No phone number available for this patient.");
    return;
  }
  try {
    setLoadingTextId(row.id);

    // Generate unique token & link. Use a crypto-strong token (the token is
    // the secret that protects the patient's record), with a Math.random
    // fallback for any environment without crypto.randomUUID.
    const token =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID().replace(/-/g, "")
        : `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
    // Put the SITE in the link (D-16). The patient opens this on a phone that
    // has never had an active site set, so a site-less link resolves against the
    // default site and a patient at any other site gets "this link isn't valid".
    // getActiveSiteId() is the staff member's current site, which is the site
    // this staged patient was just read from.
    const esignLink = `${PUBLIC_BASE_URL}/esign/${getActiveSiteId()}/${token}`;

    // Save token & link into Firestore
    await updateDoc(doc(db, getStagedPatientsPath(),row.id), {
      smsLinkSent: true,
      esignToken: token,
      esignUrl: esignLink,
      smsSentAt: serverTimestamp(),
    });

    // 🔥 Call Cloud Function to send SMS.
    // Built from FUNCTIONS_BASE like every other function call in this file.
    // It used to be the raw Cloud Run URL of PRODUCTION's sendConsentSms, which
    // meant staging texted real patients from the real Notifyre account: the
    // request never reached staging's own function, so staging's SMS_MODE=dry-run
    // was never consulted. Same function in production either way — the gen-2
    // function answers on both its cloudfunctions.net and its run.app address.
    await authedFetch(
      `${FUNCTIONS_BASE}/sendConsentSms`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
      phone: row.phone,
      link: esignLink,
      firstName: row.name.split(" ")[0], // Extracts first name automatically
      // Which site is asking. The server checks membership against THIS site
      // rather than the one baked into the deployment, so a site's own
      // reception staff are not refused. Harmless to send before the server
      // reads it — that ordering is deliberate, see the commit.
      tenantId: getActiveTenantId(),
      siteId: getActiveSiteId(),
    }),
      }
    );

    alert(`✅ SMS sent to ${row.phone}`);
    fetchStagedPatients();
  } catch (err) {
    console.error("❌ SMS send failed:", err);
    alert("Failed to send SMS. Please try again.");
  } finally {
    setLoadingTextId(null);
  }
};


  const stagedFiltered = staged.filter((p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return p.name.toLowerCase().includes(q) || p.mrn.toLowerCase().includes(q);
  });

  // KPI summaries — derived from existing state only
  const kpis = {
    staged: staged.length,
    pendingEsign: staged.filter((p) => !p.esignRequested).length,
    smsSent: staged.filter((p) => p.smsLinkSent).length,
    recentlySent: patients.length,
  };

  // Sidebar action groups — preserves every original handler
  const focusForm = () => {
    setIsAddOpen(true);
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      formRef.current?.querySelector("input")?.focus();
    });
  };

  const commandGroups = [
    {
      label: "Operations",
      icon: Workflow,
      actions: [
        // UI revamp: the personal home page, first in the sidebar. Admins only:
        // reception has no home page and stays here.
        ...(UI_REVAMP_ENABLED && isAdmin
          ? [
              {
                label: "Home",
                icon: HomeNavIcon,
                onClick: () => navigate("/home"),
                tone: "from-purple-500/30 to-violet-500/30 border-purple-300/30 text-purple-100",
              },
            ]
          : []),
        {
          label: "Add Patient",
          icon: UserPlus,
          onClick: focusForm,
          tone: "from-emerald-500/30 to-teal-500/30 border-emerald-300/30 text-emerald-100",
        },
      ],
    },
    {
      label: "Navigation",
      icon: Layers,
      actions: [
        {
          label: "Coordinator Dashboard",
          icon: UserCog,
          onClick: () => navigate("/coordinator"),
          tone: "from-amber-500/30 to-orange-500/30 border-amber-300/30 text-amber-100",
        },
      ],
    },
    {
      label: "System",
      icon: Activity,
      actions: [
        {
          label: "Log Out",
          icon: LogOut,
          onClick: handleLogout,
          tone: "from-red-500/30 to-rose-500/30 border-red-300/30 text-red-100",
        },
      ],
    },
  ];

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      {/* Mobile drawer backdrop */}
      {mobileDockOpen && (
        <div
          onClick={() => setMobileDockOpen(false)}
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
        />
      )}

      <div className="flex min-h-screen">
        {/* ============ LEFT COMMAND DOCK SIDEBAR ============ */}
        <aside
          className={`fixed lg:sticky top-0 z-50 h-screen flex flex-col bg-slate-950/60 backdrop-blur-2xl border-r border-white/10 shadow-[8px_0_40px_-20px_rgba(124,58,237,0.45)] transition-all duration-300 ease-out ${
            isCommandDockOpen ? "w-64" : "w-[72px]"
          } ${
            mobileDockOpen
              ? "translate-x-0"
              : "-translate-x-full lg:translate-x-0"
          }`}
        >
          {/* Sidebar header */}
          <div
            className={`flex p-3 border-b border-white/10 ${
              isCommandDockOpen
                ? "items-center gap-2 justify-between"
                : "flex-col items-center justify-center gap-2"
            }`}
          >
            <div className="relative shrink-0">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
              {UI_REVAMP_ENABLED && isCommandDockOpen ? (
                // The logo says RegX, so it's shown full size with no text beside it.
                <img src="/RegX zoom.png" alt="RegX" className="relative h-12 w-auto max-w-[9rem] object-contain object-left" />
              ) : (
              <div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center overflow-hidden p-1">
                <img
                  src="/RegX zoom.png"
                  alt="RegX"
                  className="w-full h-full object-contain scale-110"
                />
              </div>
              )}
            </div>
            {isCommandDockOpen && !UI_REVAMP_ENABLED && (
              <div className="flex-1 min-w-0">
                <div className="text-[9px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">
                  Clinical Staff
                </div>
                <div className="text-sm font-semibold text-white leading-tight tracking-tight truncate">
                  Reception
                </div>
              </div>
            )}
            <button
              onClick={() => setIsCommandDockOpen((o) => !o)}
              title={isCommandDockOpen ? "Collapse" : "Expand"}
              className="hidden lg:flex w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.1] hover:border-purple-300/30 items-center justify-center text-slate-300 hover:text-white transition shrink-0"
            >
              {isCommandDockOpen ? (
                <ChevronLeft className="w-3.5 h-3.5" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              )}
            </button>
          </div>

          {/* Sidebar groups */}
          <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2 px-2 space-y-3">
            {commandGroups.map((g) => {
              const GroupIcon = g.icon;
              return (
                <div key={g.label}>
                  {isCommandDockOpen ? (
                    <div className="px-1.5 mb-1.5 flex items-center gap-1.5">
                      {!UI_REVAMP_ENABLED && <GroupIcon className="w-3 h-3 text-purple-300" />}
                      <span className="text-[9px] uppercase tracking-[0.22em] text-slate-400 font-semibold">
                        {g.label}
                      </span>
                      {!UI_REVAMP_ENABLED && (
                        <span className="ml-auto text-[9px] text-slate-500">
                          {g.actions.length}
                        </span>
                      )}
                    </div>
                  ) : UI_REVAMP_ENABLED ? (
                    // Collapsed: just space between sections, no icon.
                    <div className="h-2" aria-hidden="true" />
                  ) : (
                    <div className="flex flex-col items-center gap-1 mb-1.5">
                      <GroupIcon className="w-3 h-3 text-purple-300" />
                      <span className="block w-8 h-px bg-white/10" />
                    </div>
                  )}
                  <div className="space-y-0.5">
                    {g.actions.map((a) => {
                      const ActionIcon = a.icon;
                      return (
                        <button
                          key={a.label}
                          onClick={() => {
                            a.onClick();
                            setMobileDockOpen(false);
                          }}
                          title={a.label}
                          className={`group relative w-full flex items-center rounded-lg border border-transparent hover:border-white/10 hover:bg-white/[0.06] text-slate-200 hover:text-white transition-all ${
                            isCommandDockOpen
                              ? "gap-2.5 px-2 py-1.5 justify-start"
                              : "p-1.5 justify-center"
                          }`}
                        >
                          <span
                            className={`flex items-center justify-center w-7 h-7 rounded-md bg-gradient-to-br ${a.tone} border shrink-0 transition group-hover:scale-105 group-hover:shadow-[0_0_15px_-2px_rgba(139,92,246,0.5)]`}
                          >
                            <ActionIcon className="w-3.5 h-3.5" />
                          </span>
                          {isCommandDockOpen && (
                            <span className="text-xs font-medium truncate">
                              {a.label}
                            </span>
                          )}
                          {!isCommandDockOpen && (
                            <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 rounded-md bg-slate-900/95 border border-white/10 text-[11px] text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-lg">
                              {a.label}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {UI_REVAMP_ENABLED && <SidebarThemeToggle expanded={isCommandDockOpen} />}
          </nav>

          {/* Footer mini */}
          <div className={`p-2.5 border-t border-white/10 ${isCommandDockOpen ? "" : "flex justify-center"}`}>
            {isCommandDockOpen ? (
              <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/[0.04] border border-white/10">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center ring-1 ring-white/20 shrink-0">
                  <Users className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[9px] uppercase tracking-wider text-slate-400 leading-none">
                    Live
                  </div>
                  <div className="text-xs font-semibold text-white truncate">
                    {staged.length} staged
                  </div>
                </div>
                <Sparkles className="w-3 h-3 text-purple-300 shrink-0" />
              </div>
            ) : (
              <div
                className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center ring-1 ring-white/20"
                title={`${staged.length} staged`}
              >
                <Users className="w-3.5 h-3.5 text-white" />
              </div>
            )}
          </div>
        </aside>

        {/* ============ RIGHT CONTENT COLUMN ============ */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Sticky top bar */}
          <header className="sticky top-0 z-30 backdrop-blur-2xl bg-slate-950/40 border-b border-white/10 shadow-[0_8px_30px_-12px_rgba(124,58,237,0.35)]">
            <div className="px-3 sm:px-4 py-2.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  onClick={() => setMobileDockOpen(true)}
                  title="Open menu"
                  className="lg:hidden w-9 h-9 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] flex items-center justify-center transition"
                >
                  <Menu className="w-4 h-4 text-slate-200" />
                </button>
                <div className="min-w-0">
                  {!UI_REVAMP_ENABLED && (
                    <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold">
                      Lung Innovations · RegX
                    </div>
                  )}
                  <h1 className="text-[15px] sm:text-base font-semibold tracking-tight leading-tight truncate">
                    {UI_REVAMP_ENABLED ? "Reception" : "Clinical Staff Portal · Reception"}
                  </h1>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <button
                    onClick={() => navigate("/dashboard")}
                    className="rx-header-admin-btn hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all"
                  >
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Back to Admin Dashboard
                  </button>
                )}
                {/* UI revamp: Add Patient lives in the sidebar only. */}
                {!UI_REVAMP_ENABLED && (
                <button
                  onClick={focusForm}
                  className="group relative inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(16,185,129,0.6)] active:scale-[0.98] transition-all overflow-hidden"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <Plus className="w-3.5 h-3.5 relative" />
                  <span className="relative hidden sm:inline">Add Patient</span>
                </button>
                )}
                <button
                  onClick={handleLogout}
                  title="Log out"
                  className="hidden sm:flex w-9 h-9 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-red-500/15 hover:border-red-400/30 transition-all items-center justify-center group"
                >
                  <LogOut className="w-4 h-4 text-slate-200 group-hover:text-red-300 transition" />
                </button>
              </div>
            </div>
          </header>

          <main className="flex-1 px-3 sm:px-4 py-4 space-y-3">
            {/* KPI strip */}
            <section className="rx-kpi-strip grid grid-cols-2 lg:grid-cols-4 gap-2.5">
              {[
                {
                  label: "Pre-Screened",
                  value: kpis.staged,
                  icon: Users,
                  accent: "from-purple-500/20 to-indigo-500/20",
                  valueClass: "text-white",
                },
                {
                  label: "Pending eSignatures",
                  value: kpis.pendingEsign,
                  icon: FileSignature,
                  accent: "from-amber-500/20 to-orange-500/20",
                  valueClass: "text-amber-300",
                },
                {
                  label: "SMS Sent",
                  value: kpis.smsSent,
                  icon: MessageSquare,
                  accent: "from-blue-500/20 to-cyan-500/20",
                  valueClass: "text-blue-300",
                },
                {
                  label: "Recently Sent",
                  value: kpis.recentlySent,
                  icon: Send,
                  accent: "from-emerald-500/20 to-teal-500/20",
                  valueClass: "text-emerald-300",
                },
              ].map((k) => (
                <div
                  key={k.label}
                  className="relative overflow-hidden rounded-2xl border border-white/10 hover:border-purple-300/30 bg-white/[0.03] backdrop-blur-xl px-3.5 py-2.5 transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-12px_rgba(139,92,246,0.4)] group"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${k.accent} opacity-60 pointer-events-none`} />
                  <div className="relative flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-300/80 font-medium">
                        {k.label}
                      </div>
                      <div className={`text-2xl font-bold tracking-tight leading-tight ${k.valueClass}`}>
                        {k.value}
                      </div>
                    </div>
                    <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:scale-105 transition">
                      <k.icon className="w-4 h-4 text-white/80" />
                    </div>
                  </div>
                </div>
              ))}
            </section>

            {/* Add Patient Form */}
            <section
              ref={formRef}
              className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.05] via-transparent to-blue-500/[0.05] pointer-events-none" />
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 opacity-80" />

              <div className={`relative ${isAddOpen ? "p-4" : "p-3"}`}>
                <button
                  type="button"
                  onClick={() => setIsAddOpen((o) => !o)}
                  className="w-full flex items-center gap-2 group text-left"
                  aria-expanded={isAddOpen}
                >
                  <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center group-hover:border-emerald-300/30 transition">
                    <UserPlus className="w-3.5 h-3.5 text-emerald-300" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white leading-tight">
                      Add Patient
                    </h2>
                    <p className="text-[10px] text-slate-400 leading-tight">
                      {isAddOpen
                        ? "Pre-screen intake form"
                        : "Tap to expand intake form"}
                    </p>
                  </div>
                  <span className="mx-2 h-px flex-1 bg-white/5" />
                  {!isAddOpen && (name || dob || mrn || phone || email || diagnosis.length > 0) && (
                    <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-400/15 text-amber-200 border border-amber-400/30">
                      <Sparkles className="w-3 h-3" />
                      Draft
                    </span>
                  )}
                  <span
                    className={`w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center text-slate-300 group-hover:text-white group-hover:border-emerald-300/30 transition shrink-0`}
                  >
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform duration-300 ${
                        isAddOpen ? "rotate-180" : ""
                      }`}
                    />
                  </span>
                </button>

                {isAddOpen && (
                <form onSubmit={handleAddToStage} className="space-y-3 mt-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {/* Name */}
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                        Patient Name
                      </span>
                      <div className="relative">
                        <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <input
                          type="text"
                          placeholder="Full name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                          className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
                        />
                      </div>
                    </label>

                    {/* DOB */}
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                        Date of Birth
                      </span>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <input
                          type="date"
                          value={dob}
                          onChange={(e) => setDob(e.target.value)}
                          required
                          className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition [color-scheme:dark]"
                        />
                      </div>
                    </label>

                    {/* MRN */}
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                        MRN
                      </span>
                      <div className="relative">
                        <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <input
                          type="text"
                          placeholder="Medical record #"
                          value={mrn}
                          onChange={(e) => { setMrn(e.target.value); setMrnError(""); }}
                          required
                          className={`w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:bg-white/[0.06] focus:ring-2 transition font-mono ${mrnError ? "border-red-400/70 focus:border-red-400/70 focus:ring-red-500/20" : "border-white/10 focus:border-purple-400/50 focus:ring-purple-500/20"}`}
                        />
                      </div>
                      {mrnError && (
                        <p className="mt-1 text-[11px] text-red-400 font-medium">{mrnError}</p>
                      )}
                    </label>

                    {/* Phone */}
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                        Phone
                      </span>
                      <div className="flex items-stretch rounded-xl border border-white/10 bg-white/[0.04] focus-within:border-purple-400/50 focus-within:ring-2 focus-within:ring-purple-500/20 transition overflow-hidden">
                        <div className="flex items-center gap-1.5 pl-3 pr-2.5 border-r border-white/10 shrink-0">
                          <Phone className="w-4 h-4 text-slate-500 shrink-0" />
                          <span className="text-sm text-slate-400 font-medium select-none">+1</span>
                        </div>
                        <input
                          type="tel"
                          placeholder="(555) 123-4567"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          required
                          className="flex-1 min-w-0 px-3 py-2 bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
                        />
                      </div>
                    </label>

                    {/* Email */}
                    <label className="block sm:col-span-2 lg:col-span-2">
                      <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                        Email
                      </span>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <input
                          type="email"
                          placeholder="Email Address"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
                        />
                      </div>
                    </label>
                  </div>

                  {/* Diagnosis pills */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <HeartPulse className="w-3 h-3 text-purple-300" />
                      <span className="text-[10px] uppercase tracking-wider font-medium text-slate-400">
                        Select Diagnosis
                      </span>
                      {diagnosis.length > 0 && (
                        <span className="ml-1 text-[10px] text-purple-300 font-semibold">
                          {diagnosis.length} selected
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {DIAGNOSES.map((d) => {
                        const active = diagnosis.includes(d);
                        return (
                          <button
                            type="button"
                            key={d}
                            onClick={() => {
                              if (active) {
                                setDiagnosis((prev) => prev.filter((x) => x !== d));
                              } else {
                                setDiagnosis((prev) => [...prev, d]);
                              }
                            }}
                            className={`group relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                              active
                                ? "bg-gradient-to-r from-purple-500/30 to-blue-500/30 border-purple-300/40 text-white shadow-[0_0_15px_-2px_rgba(139,92,246,0.5)]"
                                : "bg-white/[0.04] border-white/10 text-slate-300 hover:bg-white/[0.08] hover:border-purple-300/30 hover:text-white"
                            }`}
                          >
                            {active && (
                              <CheckCircle2 className="w-3 h-3 text-emerald-300" />
                            )}
                            {!active && (
                              <span className="w-3 h-3 rounded-full border border-white/20" />
                            )}
                            {diagnosisLabel(d)}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={mrnChecking}
                    className="group relative w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(16,185,129,0.6)] active:scale-[0.98] transition-all overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                    {mrnChecking ? (
                      <Loader2 className="w-4 h-4 relative animate-spin" />
                    ) : (
                      <UserPlus className="w-4 h-4 relative" />
                    )}
                    <span className="relative">{mrnChecking ? "Checking…" : "Add Patient"}</span>
                  </button>
                </form>
                )}
              </div>
            </section>

            {/* Search */}
            <section className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl px-2.5 py-2 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.5)]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search Pre-Screened patients by Name or MRN…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-9 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </section>

            {/* Pre-Screened Patients */}
            <section className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-3 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.05] via-transparent to-blue-500/[0.05] pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center">
                    <Users className="w-3.5 h-3.5 text-purple-300" />
                  </div>
                  <div>
                    <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white leading-tight">
                      Pre-Screened Patients
                    </h2>
                    <p className="text-[10px] text-slate-400 leading-tight">
                      Awaiting consent or send
                    </p>
                  </div>
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400">
                    {stagedFiltered.length} of {staged.length}
                  </span>
                </div>

                {stagedFiltered.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] backdrop-blur-xl p-10 text-center">
                    <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-white/10 flex items-center justify-center mb-3">
                      <UserPlus className="w-5 h-5 text-purple-300" />
                    </div>
                    <h3 className="text-sm font-semibold text-white">No Pre-Screened patients yet.</h3>
                    <p className="text-xs text-slate-400 mt-1">
                      Add a patient above to start a new intake.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
                    {stagedFiltered.map((row) => (
                      <StagedCard
                        key={row.id}
                        row={row}
                        onChooseFile={handleChooseFile}
                        onRequestEsign={handleRequestEsign}
                        onSend={handleSendRow}
                        onSendText={handleSendText}
                        loadingSendId={loadingSendId}
                        loadingTextId={loadingTextId}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Recently Sent Patients */}
            <section className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-3 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/[0.04] via-transparent to-cyan-500/[0.04] pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center">
                    <Send className="w-3.5 h-3.5 text-emerald-300" />
                  </div>
                  <div>
                    <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white leading-tight">
                      Recently Sent Patients
                    </h2>
                    <p className="text-[10px] text-slate-400 leading-tight">
                      Latest consents pushed forward
                    </p>
                  </div>
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400">
                    {patients.length} total
                  </span>
                </div>

                {patients.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] backdrop-blur-xl p-8 text-center">
                    <Clock className="w-5 h-5 text-slate-500 mx-auto mb-2" />
                    <p className="text-xs text-slate-400">No patients sent yet.</p>
                  </div>
                ) : (
                  <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {patients.map((p) => (
                      <li
                        key={p.id}
                        className="rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-2.5 hover:bg-white/[0.05] transition group"
                      >
                        <div className="flex items-start gap-2">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 flex items-center justify-center text-[10px] font-bold text-white ring-1 ring-white/20 shrink-0">
                            {(p.name || "?").charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-semibold text-white truncate">
                              {p.name}
                            </div>
                            <div className="text-[10px] text-slate-400 flex items-center gap-1.5 mt-0.5 flex-wrap">
                              <span className="font-mono text-purple-300/80">MRN {p.mrn}</span>
                              <span className="text-slate-500">·</span>
                              <span>{formatDiagnosis(p.diagnosis)}</span>
                            </div>
                            <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
                              <Clock className="w-3 h-3" />
                              {p.signedAt?.seconds
                                ? new Date(p.signedAt.seconds * 1000).toLocaleString()
                                : "—"}
                            </div>
                          </div>
                          {p.consentUrl && (
                            <a
                              href={p.consentUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="w-7 h-7 rounded-md bg-white/[0.04] border border-white/10 hover:bg-purple-500/15 hover:border-purple-300/30 flex items-center justify-center text-slate-300 hover:text-white transition shrink-0"
                              title="View consent"
                            >
                              <FileText className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            <div className="h-6" />
          </main>
        </div>
      </div>

      {/* eSignature Modal */}
      <ESignatureModal
        open={esignOpen}
        onClose={() => setEsignOpen(false)}
        patient={esignPatient}
        onSaved={handleEsignSaved}
      />

      {/* Name + DOB duplicate warning, layered above the Add Patient form so the
          typed values stay on screen behind it. */}
      <PotentialDuplicateDialog
        matches={dupMatches}
        entered={{ name, dob, mrn }}
        onCancel={() => setDupMatches([])}
        onProceed={async () => {
          if (
            window.confirm(
              "Add this patient anyway? A possible duplicate was found with the same date of birth."
            )
          ) {
            await performAddToStage();
          }
        }}
      />
    </div>
  );
}
